/**
 * Shared image upload helpers: HEIC/HEIF conversion for iPhone photos, validation constants.
 * HEIC is converted to JPEG in the browser before upload so the backend receives standard formats.
 */

/** MIME types the backend accepts (after any HEIC conversion). */
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

/** MIME types for HEIC/HEIF (iPhone default). Converted to JPEG before upload. */
const HEIC_MIMES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']

/** For <input accept="">: allow standard images plus HEIC so iOS users can pick photos. */
export const ACCEPT_IMAGE = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif'

export const MAX_IMAGE_BYTES = 2 * 1024 * 1024 // 2MB (logo, menu items, etc.)
/** Banner images: 4MB max. */
export const MAX_BANNER_BYTES = 4 * 1024 * 1024

/**
 * Returns true if the file is HEIC/HEIF (will need conversion).
 * @param {File} file
 * @returns {boolean}
 */
export function isHeic(file) {
  if (!file?.type) return false
  return HEIC_MIMES.includes(file.type)
}

/**
 * If the file is HEIC/HEIF, converts it to JPEG in the browser and returns a new File.
 * Otherwise returns the original file. Use before validation and upload so the API
 * always receives JPEG/PNG/GIF/WebP.
 * @param {File} file - User-selected file (may be HEIC from iPhone).
 * @returns {Promise<File>} - Same file or a new File (JPEG) from HEIC conversion.
 */
export async function prepareImageFile(file) {
  if (!file) return file
  if (!isHeic(file)) return file

  const heic2any = (await import('heic2any')).default
  const blob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  })
  const resultBlob = Array.isArray(blob) ? blob[0] : blob
  if (!resultBlob || !(resultBlob instanceof Blob)) return file

  const name = file.name.replace(/\.[^.]+$/i, '.jpg')
  return new File([resultBlob], name, { type: 'image/jpeg', lastModified: Date.now() })
}

/** @deprecated No longer used — compression is best-effort; raw file is uploaded when it fails. */
export const COMPRESS_IMAGE_ERROR_MESSAGE = 'Image could not be resized. Please choose a smaller image or try another photo.'

const MAX_DIM = 2048
/** JPEG quality steps for canvas.toBlob; slightly lower for smaller files (server also re-encodes as WebP). */
const QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45]
const MAX_SCALE_PASSES = 2

/**
 * Draw a drawable (ImageBitmap or HTMLImageElement) to canvas, resize to fit maxDim, then run toBlob loop until under maxBytes.
 * @param {CanvasImageSource} drawable
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @param {File} originalFile - for filename
 * @param {number} maxBytes
 * @returns {Promise<File>}
 */
function compressDrawableToFile(drawable, naturalWidth, naturalHeight, originalFile, maxBytes) {
  return new Promise((resolve, reject) => {
    let w = naturalWidth
    let h = naturalHeight
    if (w > MAX_DIM || h > MAX_DIM) {
      if (w >= h) {
        h = Math.round((h * MAX_DIM) / w)
        w = MAX_DIM
      } else {
        w = Math.round((w * MAX_DIM) / h)
        h = MAX_DIM
      }
    }
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      resolve(null)
      return
    }
    ctx.drawImage(drawable, 0, 0, w, h)
    let idx = 0
    let scalePasses = 0
    let toBlobWithoutQualityRetry = false

    function done(blob) {
      if (!blob) return false
      const name = originalFile.name.replace(/\.[^.]+$/i, '.jpg')
      if (blob.size <= maxBytes) {
        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
        return true
      }
      if (idx < QUALITIES.length - 1) {
        idx += 1
        tryBlob()
        return true
      }
      if (scalePasses >= MAX_SCALE_PASSES) {
        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
        return true
      }
      scalePasses += 1
      const scale = 0.7
      const nextW = Math.max(256, Math.round(w * scale))
      const nextH = Math.max(256, Math.round(h * scale))
      if (nextW >= w && nextH >= h) {
        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
        return true
      }
      w = nextW
      h = nextH
      canvas.width = w
      canvas.height = h
      ctx.drawImage(drawable, 0, 0, w, h)
      idx = 0
      tryBlob()
      return true
    }

    function tryBlob() {
      const quality = QUALITIES[idx]
      canvas.toBlob(
        (blob) => {
          if (blob) {
            done(blob)
            return
          }
          // iOS Safari can return null when quality param is used (canvas.toBlob bug). Retry without quality once.
          if (!toBlobWithoutQualityRetry) {
            toBlobWithoutQualityRetry = true
            canvas.toBlob(
              (blob2) => {
                if (done(blob2)) return
                // Still null: try half-size canvas and one more toBlob without quality (iOS workaround)
                const halfW = Math.max(256, Math.round(w / 2))
                const halfH = Math.max(256, Math.round(h / 2))
                if (halfW < w || halfH < h) {
                  canvas.width = halfW
                  canvas.height = halfH
                  ctx.drawImage(drawable, 0, 0, halfW, halfH)
                  canvas.toBlob((blob3) => { done(blob3) || resolve(null) }, 'image/jpeg')
                } else {
                  resolve(null)
                }
              },
              'image/jpeg'
            )
          } else {
            resolve(null)
          }
        },
        'image/jpeg',
        quality
      )
    }
    tryBlob()
  })
}

/**
 * Resize drawable to canvas and encode PNG (preserves alpha). Shrinks dimensions until under maxBytes or min size.
 *
 * @param {CanvasImageSource} drawable
 * @param {number} naturalWidth
 * @param {number} naturalHeight
 * @param {File} originalFile
 * @param {number} maxBytes
 * @returns {Promise<File|null>}
 */
function compressDrawableToPngFile(drawable, naturalWidth, naturalHeight, originalFile, maxBytes) {
  return new Promise((resolve) => {
    let w = naturalWidth
    let h = naturalHeight
    if (w > MAX_DIM || h > MAX_DIM) {
      if (w >= h) {
        h = Math.round((h * MAX_DIM) / w)
        w = MAX_DIM
      } else {
        w = Math.round((w * MAX_DIM) / h)
        h = MAX_DIM
      }
    }

    const name = originalFile.name.replace(/\.[^.]+$/i, '.png')

    function attempt() {
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { alpha: true })
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(drawable, 0, 0, w, h)
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null)
          return
        }
        if (blob.size <= maxBytes || (w <= 256 && h <= 256)) {
          resolve(new File([blob], name, { type: 'image/png', lastModified: Date.now() }))
          return
        }
        w = Math.max(256, Math.round(w * 0.85))
        h = Math.max(256, Math.round(h * 0.85))
        attempt()
      }, 'image/png')
    }

    attempt()
  })
}

/**
 * Load file via createImageBitmap. Optionally decode at smaller size to help iOS with large camera photos.
 * @param {File} file
 * @param {{ resizeWidth?: number, imageOrientation?: string }} [opts]
 * @returns {Promise<{ drawable: ImageBitmap, width: number, height: number, close: function } | null>}
 */
function loadImageDrawableViaCreateImageBitmap(file, opts = {}) {
  if (typeof createImageBitmap !== 'function') return Promise.resolve(null)
  return createImageBitmap(file, opts)
    .then((bitmap) => ({
      drawable: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
    }))
    .catch(() => null)
}

function loadImageDrawableWithBlobUrl(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ drawable: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })
}

function loadImageDrawableWithDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result
      if (typeof dataUrl !== 'string') {
        resolve(null)
        return
      }
      const img = new Image()
      img.onload = () => resolve({ drawable: img, width: img.naturalWidth, height: img.naturalHeight, close: () => {} })
      img.onerror = () => resolve(null)
      img.src = dataUrl
    }
    reader.onerror = () => resolve(null)
    reader.readAsDataURL(file)
  })
}

/**
 * Best-effort image compression. Tries multiple strategies to shrink the file.
 * If ALL compression paths fail (e.g. iOS canvas.toBlob bug), returns the original file
 * so the server can accept and resize it (the backend already resizes all uploads).
 * Never rejects — always resolves with a File.
 * @param {File} file - Image file (JPEG/PNG/GIF/WebP).
 * @param {number} maxBytes - Target size in bytes. Best-effort; result may be larger.
 * @param {{ preferPng?: boolean }} [options] - When `preferPng` or PNG file type, use PNG pipeline (keeps transparency).
 * @returns {Promise<File>} - Compressed file, or original file if compression failed.
 */
export async function compressImageToMaxBytes(file, maxBytes, options = {}) {
  if (!file || file.size <= maxBytes) return file

  const usePngPipeline =
    options.preferPng === true ||
    (file.type === 'image/png' && options.preferPng !== false)

  async function tryLoadAndCompressPng(loadFn) {
    const loaded = await loadFn()
    if (!loaded || !loaded.drawable) return null
    try {
      const result = await compressDrawableToPngFile(
        loaded.drawable,
        loaded.width,
        loaded.height,
        file,
        maxBytes
      )
      if (loaded.close) loaded.close()
      return result || null
    } catch {
      if (loaded.close) loaded.close()
      return null
    }
  }

  if (usePngPipeline) {
    let pngResult = await tryLoadAndCompressPng(() =>
      loadImageDrawableViaCreateImageBitmap(file, { resizeWidth: MAX_DIM, imageOrientation: 'none' })
    )
    if (pngResult && pngResult.size <= maxBytes) return pngResult

    pngResult = await tryLoadAndCompressPng(() => loadImageDrawableViaCreateImageBitmap(file))
    if (pngResult && pngResult.size <= maxBytes) return pngResult

    pngResult = await tryLoadAndCompressPng(() => loadImageDrawableWithDataUrl(file))
    if (pngResult && pngResult.size <= maxBytes) return pngResult

    pngResult = await tryLoadAndCompressPng(() => loadImageDrawableWithBlobUrl(file))
    if (pngResult) return pngResult

    return file
  }

  async function tryLoadAndCompress(loadFn) {
    const loaded = await loadFn()
    if (!loaded || !loaded.drawable) return null
    try {
      const result = await compressDrawableToFile(loaded.drawable, loaded.width, loaded.height, file, maxBytes)
      if (loaded.close) loaded.close()
      return result || null
    } catch {
      if (loaded.close) loaded.close()
      return null
    }
  }

  // 1) createImageBitmap with resize
  let result = await tryLoadAndCompress(() =>
    loadImageDrawableViaCreateImageBitmap(file, { resizeWidth: MAX_DIM, imageOrientation: 'none' })
  )
  if (result && result.size <= maxBytes) return result

  // 2) createImageBitmap without options
  result = await tryLoadAndCompress(() => loadImageDrawableViaCreateImageBitmap(file))
  if (result && result.size <= maxBytes) return result

  // 3) Data URL (FileReader → Image)
  result = await tryLoadAndCompress(() => loadImageDrawableWithDataUrl(file))
  if (result && result.size <= maxBytes) return result

  // 4) Blob URL (Image + object URL)
  result = await tryLoadAndCompress(() => loadImageDrawableWithBlobUrl(file))
  if (result && result.size <= maxBytes) return result

  // 5) compressorjs
  try {
    const Compressor = (await import('compressorjs')).default
    const compressed = await new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.75,
        maxWidth: MAX_DIM,
        maxHeight: MAX_DIM,
        checkOrientation: false,
        success(r) {
          const name = file.name.replace(/\.[^.]+$/i, '.jpg')
          resolve(r instanceof File ? r : new File([r], name, { type: 'image/jpeg', lastModified: Date.now() }))
        },
        error: reject,
      })
    })
    if (compressed && compressed.size <= maxBytes) return compressed
    if (compressed) return compressed
  } catch { /* ignore */ }

  // 6) browser-image-compression
  try {
    const imageCompression = (await import('browser-image-compression')).default
    const compressed = await imageCompression(file, {
      maxSizeMB: maxBytes / (1024 * 1024),
      maxWidthOrHeight: MAX_DIM,
      useWebWorker: false,
      fileType: 'image/jpeg',
      initialQuality: 0.78,
      preserveExif: false,
    })
    if (compressed && compressed.size <= maxBytes) return compressed
    if (compressed) return compressed
  } catch { /* ignore */ }

  // All compression failed — return the original file. Server will resize it.
  return file
}

/**
 * Wide/tall logos cannot fit entirely inside a square crop that stays within the image pixels
 * (max square side = min(width, height)). Letterbox onto a square canvas so a 1:1 stencil can
 * frame the full artwork.
 *
 * @param {string} src - Object URL, blob URL, or readable image URL
 * @param {{ fillColor?: string | null }} [opts] - Omitted or `null`: transparent padding (PNG) so black-on-transparent logos stay visible. Set e.g. `'#000000'` for solid bars.
 * @returns {Promise<{ url: string, revokeIfNeeded: () => void }>}
 */
export async function prepareSquareLogoCropSource(src, opts = {}) {
  const fillColor = opts.fillColor === undefined ? null : opts.fillColor
  /** Avoid canvas / memory failures on phone camera originals */
  const MAX_PAD_SIDE = 4096

  const img = new Image()
  img.decoding = 'async'
  // Omit crossOrigin so blob:/object URLs always load; canvas is same-origin for those.

  await new Promise((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('Image failed to load'))
    img.src = src
  })

  let w = img.naturalWidth
  let h = img.naturalHeight
  if (!w || !h) {
    return { url: src, revokeIfNeeded: () => {} }
  }

  /** @type {CanvasImageSource} */
  let drawable = img
  if (Math.max(w, h) > MAX_PAD_SIDE) {
    const scale = MAX_PAD_SIDE / Math.max(w, h)
    const sw = Math.max(1, Math.floor(w * scale))
    const sh = Math.max(1, Math.floor(h * scale))
    const down = document.createElement('canvas')
    down.width = sw
    down.height = sh
    const dctx = down.getContext('2d', { alpha: true })
    if (!dctx) {
      return { url: src, revokeIfNeeded: () => {} }
    }
    dctx.clearRect(0, 0, sw, sh)
    dctx.drawImage(img, 0, 0, sw, sh)
    drawable = down
    w = sw
    h = sh
  }

  // Already square (±2px): use source as-is (no second encode)
  if (Math.abs(w - h) <= 2) {
    return { url: src, revokeIfNeeded: () => {} }
  }

  const side = Math.max(w, h)
  const canvas = document.createElement('canvas')
  canvas.width = side
  canvas.height = side
  const ctx = canvas.getContext('2d', { alpha: true })
  if (!ctx) {
    return { url: src, revokeIfNeeded: () => {} }
  }

  ctx.clearRect(0, 0, side, side)
  if (fillColor != null && fillColor !== '') {
    ctx.fillStyle = fillColor
    ctx.fillRect(0, 0, side, side)
  }
  ctx.drawImage(drawable, (side - w) / 2, (side - h) / 2, w, h)

  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/png')
  })

  if (!blob) {
    return { url: src, revokeIfNeeded: () => {} }
  }

  const url = URL.createObjectURL(blob)
  return {
    url,
    revokeIfNeeded: () => URL.revokeObjectURL(url),
  }
}
